import axios from "axios"

// Pre-auth endpoints: aucun Sanctum token requis, le lead n'a pas encore de compte.
// Miroir de stageFunnelService sur le contrat /v1/inscription-funnel/*.
//
// `updateInscriptionAnswers` porte TOUT le parcours : diagnostic ET formule
// (mode de cours, engagement, créneaux, matières, date). Une seule route
// d'écriture, donc une seule source du devis renvoyé en retour. Pas de
// `program` : l'étape programme n'existe pas dans ce funnel.

// Map frontend camelCase state keys → snake_case wire format expected by Laravel.
const camelToSnakeAnswers = (data) => {
    if (!data) return data
    const mapping = {
        studentClass: "student_class",
        besoinsSpeciaux: "besoins_speciaux",
        subjectGoals: "subject_goals",
        leadIntent: "lead_intent",
        startDate: "start_date",
        submissionGUID: "submission_guid",
        offerType: "offer_type",
        weeklyHours: "weekly_hours",
        // `false` voyage : seuls `undefined` et `null` sont retirés plus bas.
        withCommitment: "with_commitment",
        // Créneaux au format LOCAL du parent ; le serveur les canonicalise.
        slotsPossible: "slots_possible",
        hoursAllocation: "hours_allocation",
        // Seulement à l'entrée, et seulement pour forcer une variante en test.
        formatStep: "format_step",
        formatStepCapable: "format_step_capable",
    }
    return Object.fromEntries(
        Object.entries(data)
            // Drop undefined/null values so the backend's `nullable` rules
            // treat them as not sent (partiels OK sur /answers).
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [mapping[k] || k, normalizeWireValue(k, v)])
    )
}

// Les chips manipulent des ids de matières en STRING ; le wire les porte en
// int (le backend valide des ids numériques). Une seule couture, ici. Les
// tables indexées par matière (heures, cran visé) gardent leurs clés : c'est
// un objet JSON, la clé reste une chaîne des deux côtés.
const normalizeWireValue = (key, value) => {
    if (key === "subjects" && Array.isArray(value)) {
        return value.map((id) => Number(id))
    }
    return value
}

// Les trois portes qui peuvent OUVRIR un dossier disent au serveur que cet
// écran sait afficher la page du format : il ne tire une disposition que pour
// un client qui l'annonce. Un bundle d'avant cette page n'envoie rien, son
// dossier n'est pas tiré, et il reste hors de la mesure.
const KNOWS_THE_FORMAT_PAGE = { formatStepCapable: true }

/**
 * Hydrate le funnel Inscription depuis un submissionGUID HubSpot. Le backend
 * find-or-create le lead et pose l'état `inscription_started`. Renvoie
 * funnel_state_id, l'utilisateur pré-rempli et la config (plancher de date).
 *
 * Peut répondre 422 `already_subscribed` : une famille déjà abonnée n'entre pas
 * dans un funnel d'ACQUISITION (elle créerait un doublon d'abonnement, et
 * atteindre l'écran de paiement fermerait ses objectifs en cours). Le lead est
 * tout de même capturé — l'écran redirige, il n'affiche pas une erreur brute.
 */
function initInscriptionFromHubSpot({}, data) {
    return axios.post(
        `/api/v1/inscription-funnel/init`,
        camelToSnakeAnswers({ ...data, ...KNOWS_THE_FORMAT_PAGE })
    )
}

// Entrée directe (sans GUID) : firstname, student_class, email.
function startInscriptionFunnel({}, data) {
    return axios.post(
        `/api/v1/inscription-funnel/start`,
        camelToSnakeAnswers({ ...data, ...KNOWS_THE_FORMAT_PAGE })
    )
}

// Persiste classe, spécificités, matières et crans visés, mode de cours,
// engagement, volume, créneaux, date (partiels OK) et RENVOIE le devis serveur.
// Auto-transition `inscription_diag_completed` dès que la question des
// spécificités a été répondue.
function updateInscriptionAnswers({ stateId }, data) {
    return axios.put(
        `/api/v1/inscription-funnel/${stateId}/answers`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Matières proposables — chapitres validés pour la classe. Réponse :
 * { data: [{ id, name, slug, icon_path, color, rank }] }.
 */
function getInscriptionSubjects({ studentClass }) {
    return axios.get(`/api/v1/inscription-funnel/subjects`, {
        params: { student_class: studentClass },
    })
}

/**
 * Tuteurs (« masteurs ») qui correspondent au profil de l'enfant — étape « aha
 * moment » juste avant le paiement. Le calcul est celui de l'été, appelé et non
 * recopié ({@link SummerFunnelController::buildMatchingTutorsPayload}), donc ce
 * funnel hérite des règles de pôle et de comptage sans rien à maintenir. Étape
 * NON bloquante : en cas d'erreur ou de réponse vide, l'écran retombe sur un
 * repli gracieux.
 */
function getInscriptionMatchingTutors({ stateId }, data) {
    return axios.post(
        `/api/v1/inscription-funnel/${stateId}/matching-tutors`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Crée la Checkout Session Stripe embarquée du 1 € de réservation et renvoie
 * son `client_secret` + le devis.
 *
 * Le jeton `user` (email en base64) est OBLIGATOIRE : la route est derrière
 * `auth_or_email` parce qu'elle ouvre un paiement et écrit sur l'élève.
 *
 * `data.payment_collection_day` : le jour de prélèvement choisi par le parent.
 *
 * Peut répondre 409 `already_subscribed` (le lead a souscrit entre-temps) ou
 * 422 si la formule est incomplète — dans les deux cas l'écran affiche le
 * message du serveur plutôt qu'un « réessayez » générique.
 */
function createInscriptionCheckoutSession({ stateId }, data, queryParams) {
    return axios.post(
        `/api/v1/inscription-funnel/${stateId}/create-checkout-session`,
        data,
        { params: queryParams }
    )
}

/**
 * Reprise depuis un lien magique : GET /inscription-funnel/resume?user=<base64email>
 * derrière `auth_or_email`. Nommé `resumeInscription` (et non `resume`) car le
 * reducer api indexe l'état par nom de fonction — un homonyme entrerait en
 * collision avec les `resume`/`resumeSummer`/`resumeStage` des autres funnels.
 */
function resumeInscription({}, {}, queryParams) {
    return axios.get(`/api/v1/inscription-funnel/resume`, {
        // `1` et non `true` : en query, c'est la forme qu'un booléen Laravel lit.
        params: { ...queryParams, format_step_capable: 1 },
    })
}

// Les seuls statuts où le funnel répond par un `message` écrit pour le parent.
const READABLE_REFUSALS = [409, 422]

/**
 * Ce qu'un refus des réponses dit au parent : la première erreur de champ,
 * sinon le message du serveur, sinon `fallback`. Un refus se LIT — le parent
 * doit savoir quoi corriger.
 *
 * Hors d'un refus (panne, quota, passerelle), le corps est celui du framework,
 * en anglais : l'écran garde son propre message.
 */
export const answersRefusalMessage = (err, fallback) => {
    const { status, data: body } = err?.response || {}
    if (!READABLE_REFUSALS.includes(status)) return fallback
    return (
        Object.values(body?.errors || {})
            .flat()
            .find(Boolean) ||
        body?.message ||
        fallback
    )
}

/**
 * Ce que l'ouverture du paiement dit au parent quand elle échoue. Même règle
 * pour le `message` ; cette route écrit aussi ses refus sous `error` (lien
 * invalide, formule incomplète), qui passent avant le message de l'écran.
 */
export const checkoutRefusalMessage = (err, fallback) =>
    answersRefusalMessage(err, err?.response?.data?.error || fallback)

export const inscriptionFunnelService = {
    initInscriptionFromHubSpot,
    startInscriptionFunnel,
    updateInscriptionAnswers,
    getInscriptionSubjects,
    getInscriptionMatchingTutors,
    createInscriptionCheckoutSession,
    resumeInscription,
}
